import { useState, useEffect, useRef } from 'react'
import { Play, Pause, Square, Clock } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function CountdownTimer() {
  const [hours, setHours] = useState(0)
  const [minutes, setMinutes] = useState(0)
  const [seconds, setSeconds] = useState(0)
  const [timeLeft, setTimeLeft] = useState(0)
  const [isRunning, setIsRunning] = useState(false)
  const [isFinished, setIsFinished] = useState(false)
  const intervalRef = useRef(null)

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setIsRunning(false)
            setIsFinished(true)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } else {
      clearInterval(intervalRef.current)
    }

    return () => clearInterval(intervalRef.current)
  }, [isRunning, timeLeft])

  const formatTime = (totalSeconds) => {
    const h = Math.floor(totalSeconds / 3600)
    const m = Math.floor((totalSeconds % 3600) / 60)
    const s = totalSeconds % 60
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }

  const startTimer = () => {
    const total = hours * 3600 + minutes * 60 + seconds
    if (total > 0) {
      setTimeLeft(total)
      setIsRunning(true)
      setIsFinished(false)
    }
  }

  const pauseTimer = () => {
    setIsRunning(false)
  }

  const resetTimer = () => {
    setIsRunning(false)
    setTimeLeft(0)
    setIsFinished(false)
    setHours(0)
    setMinutes(0)
    setSeconds(0)
  }

  return (
    <div className="space-y-6">
      <Card>
        <Card.Body>
          <div className="text-center mb-6">
            <div className="text-6xl font-bold text-primary-600 mb-4">
              {formatTime(timeLeft || hours * 3600 + minutes * 60 + seconds)}
            </div>
            {isFinished && (
              <div className="text-xl text-red-600 font-semibold mb-4">Time's Up!</div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div>
              <label className="label">Hours</label>
              <input
                type="number"
                value={hours}
                onChange={(e) => setHours(Math.max(0, parseInt(e.target.value) || 0))}
                min="0"
                max="23"
                className="input w-full"
                disabled={isRunning}
              />
            </div>
            <div>
              <label className="label">Minutes</label>
              <input
                type="number"
                value={minutes}
                onChange={(e) => setMinutes(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                min="0"
                max="59"
                className="input w-full"
                disabled={isRunning}
              />
            </div>
            <div>
              <label className="label">Seconds</label>
              <input
                type="number"
                value={seconds}
                onChange={(e) => setSeconds(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                min="0"
                max="59"
                className="input w-full"
                disabled={isRunning}
              />
            </div>
          </div>

          <div className="flex gap-4 justify-center">
            {!isRunning ? (
              <Button onClick={startTimer} variant="primary" disabled={timeLeft === 0 && hours === 0 && minutes === 0 && seconds === 0}>
                <Play className="w-4 h-4 mr-2" />
                Start
              </Button>
            ) : (
              <Button onClick={pauseTimer} variant="secondary">
                <Pause className="w-4 h-4 mr-2" />
                Pause
              </Button>
            )}
            <Button onClick={resetTimer} variant="outline">
              <Square className="w-4 h-4 mr-2" />
              Reset
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}

